/**
 * SEARCH-002 — État VIDE de la palette (⌘K) : 2 colonnes (Aller à + Créer |
 * Filtrer par + Récents).
 *
 * Extrait mécaniquement de `SearchPaletteBody` (aucun changement de logique).
 */

import { useTranslation } from 'react-i18next'
import { Clock, X } from 'lucide-react'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { type SearchPaletteApi } from './useSearchPalette'
import { CREATE_ACTIONS, GOTO_ACTIONS } from './searchPaletteHelpers'
import { ActionLinesGroup, ScopeFilterChips } from './SearchPaletteBodyParts'

export function SearchPaletteEmptyState({ palette }: { palette: SearchPaletteApi }) {
    const { t } = useTranslation()
    const { hasPermission, scopableTypes, scopeType, setInput, recents, clearRecents, removeRecent, go } = palette
    return (
        <div className="grid grid-cols-1 gap-x-4 p-1 md:grid-cols-2">
            {/* Colonne gauche : actions (lignes) */}
            <div>
                <ActionLinesGroup
                    heading={t('globalSearch.group.goto')}
                    actions={GOTO_ACTIONS}
                    hasPermission={hasPermission}
                    onGo={go}
                />
                <ActionLinesGroup
                    heading={t('globalSearch.band.create')}
                    actions={CREATE_ACTIONS}
                    hasPermission={hasPermission}
                    onGo={go}
                />
            </div>
            {/* Colonne droite : recherche (pastilles + récents) */}
            <div>
                {!scopeType && scopableTypes.length > 0 && (
                    <ScopeFilterChips types={scopableTypes} onPick={palette.pickScope} />
                )}
                {recents.length > 0 && (
                    <CommandGroup
                        heading={
                            <span className="flex items-center justify-between">
                                {t('globalSearch.group.recent')}
                                <button
                                    type="button"
                                    onClick={clearRecents}
                                    className="rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                                >
                                    {t('globalSearch.recents.clear')}
                                </button>
                            </span>
                        }
                    >
                        {recents.map((q) => (
                            <CommandItem
                                key={`recent-q:${q}`}
                                value={`recent-q:${q}`}
                                data-nav={`recent-q:${q}`}
                                onSelect={() => setInput(q)}
                                className="group text-muted-foreground"
                            >
                                <Clock className="text-muted-foreground" aria-hidden="true" />
                                <span className="flex-1 truncate">{q}</span>
                                {/* Suppression CIBLÉE d'une ligne (≠ tout effacer) — MÊME
                                    mécanique que le drawer d'aide (`HelpRecents`) : petite
                                    CROIX neutre discrète, masquée par défaut et révélée au
                                    survol de la ligne (`group-hover`) ou au focus clavier,
                                    sans décaler le texte (slot réservé, opacité).
                                    `stopPropagation` empêche cmdk de re-saisir la requête. */}
                                <button
                                    type="button"
                                    aria-label={t('globalSearch.recents.remove', { q })}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeRecent(q)
                                    }}
                                    // TACTILE : TOUJOURS visible < sm (pas de survol sur
                                    // mobile → anti-pattern « hover-only ») ; sur desktop (≥ sm)
                                    // masquée et révélée au survol/focus de la ligne. Cible
                                    // agrandie en mobile (h-9) ; réduite sur desktop (h-6).
                                    className="mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-100 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-95 sm:h-6 sm:w-6 sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                                </button>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}
            </div>
        </div>
    )
}
